import React, { useState, FormEvent, ChangeEvent, useRef, useCallback } from "react";
import { useHistory } from "react-router";
import { toBuffer } from 'ethereumjs-util';
import QRCode from "qrcode.react";
import Modal from "react-modal";
import Webcam from "react-webcam";
import { typedSignatureHash, signTypedMessage, TypedDataUtils } from 'eth-sig-util';

import useBrain from '../../hooks/brain';
import WalletConnect from '../WalletConnect';
import { useWallet } from "../../wallet-context";
import Layout from "../../components/Layout";
import { AxiosError } from "axios";

function loadXHR(url: string): Promise<Blob> {
  return new Promise(function(resolve, reject) {
      try {
          const xhr = new XMLHttpRequest();
          xhr.open("GET", url);
          xhr.responseType = "blob";
          xhr.onerror = function() {reject("Network error.")};
          xhr.onload = function() {
              if (xhr.status === 200) {resolve(xhr.response)}
              else {reject("Loading error:" + xhr.statusText)}
          };
          xhr.send();
      }
      catch(err) {
        reject(err);
      }
  });
}

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
  },
};

// Make sure to bind modal to your appElement (https://reactcommunity.org/react-modal/accessibility/)
Modal.setAppElement("#root");

const NewEngagement: React.FC = () => {
  const brain = useBrain();
  const [loading, setLoading] = useState(false);
  const { web3, walletAddress } = useWallet();

  const fileInput = useRef<HTMLInputElement>(null);
  const [signLink, setSignLink] = useState<string>();
  const [description, setDescription] = useState<string>('Nice description');

  const [preview, setPreview] = useState<string>();
  const [toggleCapture, setToggleCapture] = useState<boolean>(false);

  const webcamRef = React.useRef<any>(null);

  const capture = React.useCallback(
    () => {
      const imageSrc = webcamRef.current.getScreenshot();

      setPreview(imageSrc as string);
      setToggleCapture(false)
    },
    [webcamRef]
  );

  const videoConstraints = {
    height: 300,
    width: 350,
    facingMode: "user"
  };


  if (!walletAddress) {
    return (
      <WalletConnect />
    );
  }

  const uploadImage = async (): Promise<string | null> => {
    if (preview) {
      const blob = await loadXHR(preview);
      return brain.uploadImage(blob);
    }

    if (fileInput.current!.files) {
      return brain.uploadImage(fileInput.current!.files[0]);
    }
    return null;
  };

  const signEngagement = async (ipfsHash: string): Promise<void> => {
    if (!web3) {
      return;
    }

    const typedData = brain.getTypedData(ipfsHash);

    const provider = web3.currentProvider as any;

    return new Promise((resolve, reject) => {
      provider.send({
        method: 'eth_signTypedData_v4',
        params: [walletAddress, JSON.stringify(typedData)]
      }, async (err: any, res: any) => {
        if (err) {
          reject(err);
          return;
        }

        const signature = res.result;
        resolve(signature);
      });
    });
  };

  const imageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileReader = new FileReader();

    if (event.target.files) {
      fileReader.addEventListener(
        "load",
        function () {
          setPreview(fileReader.result as string);
        },
        false
      );

      fileReader.readAsDataURL(event.target.files[0]);
    }
  };
  // const onClick = useCallback(async () => {
  //   setIsOpen(true);
  //   navigator.mediaDevices.getUserMedia({video: true})
  //   .then(async mediaStream => {
  //     (document.querySelector('video')! as HTMLMediaElement).srcObject = mediaStream;
  //     alert(100000000)
  //     const track = mediaStream.getVideoTracks()[0];
  //     const imageCapture = new (window as any).ImageCapture(track);
  //     const detector = new (window as any).BarcodeDetector()
  //     setTimeout(async () => detector.detect(await imageCapture.grabFrame()), 10000);
  //   })
  // }, [])


  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    setLoading(true);

    try {
      const imageIpfsHash = await uploadImage();

      const ipfsHash = await brain.uploadNft({
        index: 0,
        description,
        image: `ipfs://${imageIpfsHash}`,
      });

      const signature = await signEngagement(ipfsHash);

      const link = `${window.location.protocol}//${window.location.host}/sign/${ipfsHash}/${signature}`
      console.log(link);
      setSignLink(link);
    } catch (err) {
      if (err) {
        alert((err as any).message);
        const e = err as AxiosError;
        alert(JSON.stringify(e.response?.data));
      } else {
        alert('fail');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading</div>;
  }

  return (
    <Layout>
      <main>
        <section >
          <div className="hero min-h-screen" >
            <div className="text-center hero-content text-neutral-content">
              <div className="max-w-md">
                <h1 className="mb-5 text-5xl font-bold">
                  New Engagement
                </h1>
                {toggleCapture &&
                  <>
                    <Webcam
                      audio={false}
                      height={300}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      width={350}
                      videoConstraints={videoConstraints}
                    />
                    <button className="btn btn-primary" onClick={capture}>Capture Photo</button>
                  </>
                }
                {!toggleCapture &&
                  <button className="btn btn-primary" onClick={() => { setToggleCapture(true) }}>Toggle Photo</button>
                }
                <form onSubmit={onSubmit}>
                  <div>{walletAddress}</div>
                  <div className="p-10 card bg-base-200">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Description</span>
                      </label>
                      <textarea className="textarea h-24" value={description} onChange={(event) => setDescription(event.target.value)}></textarea>
                    </div>
                  </div>
                  {preview && <img src={preview} alt="preview" />}
                  <input ref={fileInput} type="file" title="Upload Picture" onChange={imageChange} />
                  <button className="btn btn-primary" type="submit">Submit</button>
                </form>
              </div>
            </div>
          </div>
          {signLink && (
            <Modal
              isOpen={signLink !== undefined}
              onRequestClose={() => setSignLink(undefined)}
              style={customStyles}
              contentLabel="Example Modal"
            >
              <QRCode value={signLink} />
            </Modal>
          )}
        </section >
      </main >
    </Layout >
  );
};

export default NewEngagement;
